import {
  WORD_LOOKUP_CONTENT_TYPE_DEFS,
  WORD_LOOKUP_CONTENT_TYPE_IDS,
  getDefaultWordLookupEnabledOrder,
} from '@/config/wordLookupContentTypes.js'
import { TAB_LABELS } from '@/views/englishLearning/wordLookup/wordLookupSections.js'

const KNOWN_ID_SET = new Set(WORD_LOOKUP_CONTENT_TYPE_IDS)

/** 用户显式关闭全部类型时入库/回显（与 null/'' 未配置区分） */
export const WORD_LOOKUP_ALL_DISABLED_MARKER = '_none'

function filterKnownIds (list = []) {
  return (list || []).map((id) => String(id || '').trim()).filter((id) => KNOWN_ID_SET.has(id))
}

/**
 * 提交服务端 / 写入本地：竖线分隔的已启用类型 id，顺序即展示顺序
 * @returns {string}
 */
export function serializeWordLookupContentTypes (enabledOrder = []) {
  return filterKnownIds(enabledOrder).join('|')
}

/** 是否为默认顺序下的全部 16 类型（与后端 null/'' 语义一致） */
export function isDefaultWordLookupContentOrder (enabledOrder = []) {
  const defaultOrder = getDefaultWordLookupEnabledOrder()
  const list = filterKnownIds(enabledOrder)
  if (list.length !== defaultOrder.length) return false
  return list.every((id, index) => id === defaultOrder[index])
}

/** 提交 API：默认全开时传 ''，全部关闭传 {@link WORD_LOOKUP_ALL_DISABLED_MARKER}，否则竖线串 */
export function serializeWordLookupContentTypesForApi (enabledOrder = []) {
  const list = filterKnownIds(enabledOrder)
  if (!list.length) return WORD_LOOKUP_ALL_DISABLED_MARKER
  if (isDefaultWordLookupContentOrder(list)) return ''
  return serializeWordLookupContentTypes(list)
}

/**
 * 解析 set_show 回显 / 本地缓存（兼容竖线串、JSON 数组、JSON 字符串）
 * @returns {string[] | null} null=未配置（前端按默认全开）；[]=用户显式全关
 */
export function parseWordLookupContentTypes (raw) {
  if (raw === undefined || raw === null) return null
  if (Array.isArray(raw)) return filterKnownIds(raw)
  const text = String(raw).trim()
  if (text === WORD_LOOKUP_ALL_DISABLED_MARKER) return []
  if (!text) return null
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed)) return filterKnownIds(parsed)
    } catch {
      /* fall through */
    }
  }
  return filterKnownIds(text.split('|'))
}

/**
 * @returns {{ enabledOrder: string[], unaddedOrder: string[], orderIndex: Map<string, number> }}
 */
export function normalizeWordLookupContentSettings (raw) {
  if (raw && typeof raw === 'object' && raw.orderIndex instanceof Map && Array.isArray(raw.enabledOrder)) {
    return raw
  }
  let parsed
  if (Array.isArray(raw)) {
    parsed = filterKnownIds(raw)
  } else {
    parsed = parseWordLookupContentTypes(raw)
  }
  const enabledOrder = parsed === null ? [...getDefaultWordLookupEnabledOrder()] : parsed
  const enabledSet = new Set(enabledOrder)
  const unaddedOrder = WORD_LOOKUP_CONTENT_TYPE_IDS.filter((id) => !enabledSet.has(id))
  const orderIndex = new Map(enabledOrder.map((id, index) => [id, index]))
  return { enabledOrder, unaddedOrder, orderIndex }
}

export function isWordLookupContentEnabled (settings, key) {
  if (!settings?.orderIndex) return true
  return settings.orderIndex.has(key)
}

export function sortTabsByContentSettings (tabs = [], settings) {
  if (!settings?.orderIndex) return tabs
  if (settings.orderIndex.size === 0) return []
  return tabs
    .filter((tab) => settings.orderIndex.has(tab.key))
    .sort((a, b) => (settings.orderIndex.get(a.key) ?? 999) - (settings.orderIndex.get(b.key) ?? 999))
}

export function labelForWordLookupContentType (id) {
  return TAB_LABELS[id] || WORD_LOOKUP_CONTENT_TYPE_DEFS.find((d) => d.id === id)?.label || id
}

/** 供设置 UI：从 enabledOrder 构建已显示 / 未显示列表（数组原样使用，含 []） */
export function splitWordLookupContentLists (enabledOrder) {
  const ids = Array.isArray(enabledOrder)
    ? filterKnownIds(enabledOrder)
    : normalizeWordLookupContentSettings(enabledOrder).enabledOrder
  const enabledSet = new Set(ids)
  const added = ids.map((id) => ({
    id,
    label: labelForWordLookupContentType(id),
  }))
  const unadded = WORD_LOOKUP_CONTENT_TYPE_IDS.filter((id) => !enabledSet.has(id)).map((id) => ({
    id,
    label: labelForWordLookupContentType(id),
  }))
  return { added, unadded }
}

export function moveWordLookupContentItem (enabledOrder, id, direction) {
  const list = [...enabledOrder]
  const idx = list.indexOf(id)
  if (idx < 0) return list
  const next = direction === 'up' ? idx - 1 : idx + 1
  if (next < 0 || next >= list.length) return list
  const tmp = list[idx]
  list[idx] = list[next]
  list[next] = tmp
  return list
}

function getWordLookupContentSection (id) {
  return WORD_LOOKUP_CONTENT_TYPE_DEFS.find((d) => d.id === id)?.section || null
}

/** 与 modelValue 顺序一致：同一查词大区块（core/extended/practice/network）内的已启用 id */
export function getWordLookupContentSectionSiblings (enabledOrder = [], id) {
  const section = getWordLookupContentSection(id)
  if (!section) return []
  return filterKnownIds(enabledOrder).filter(
    (itemId) => getWordLookupContentSection(itemId) === section
  )
}

export function canMoveWordLookupContentItemInSection (enabledOrder, id, direction) {
  const siblings = getWordLookupContentSectionSiblings(enabledOrder, id)
  const sectionIdx = siblings.indexOf(id)
  if (sectionIdx < 0) return false
  if (direction === 'up') return sectionIdx > 0
  return sectionIdx < siblings.length - 1
}

/** 设置页分组展示：仅在同一区块内与相邻项交换（与上下箭头视觉一致） */
export function moveWordLookupContentItemInSection (enabledOrder, id, direction) {
  const siblings = getWordLookupContentSectionSiblings(enabledOrder, id)
  const sectionIdx = siblings.indexOf(id)
  if (sectionIdx < 0) return [...enabledOrder]
  const targetIdx = direction === 'up' ? sectionIdx - 1 : sectionIdx + 1
  if (targetIdx < 0 || targetIdx >= siblings.length) return [...enabledOrder]
  const list = [...enabledOrder]
  const i = list.indexOf(id)
  const j = list.indexOf(siblings[targetIdx])
  if (i < 0 || j < 0) return list
  const tmp = list[i]
  list[i] = list[j]
  list[j] = tmp
  return list
}

/** 拖拽：同区块内将 fromId 移到 toId 所在位置（按 modelValue 序） */
export function reorderWordLookupContentItemInSection (enabledOrder, fromId, toId) {
  if (!fromId || !toId || fromId === toId) return [...enabledOrder]
  if (getWordLookupContentSection(fromId) !== getWordLookupContentSection(toId)) {
    return [...enabledOrder]
  }
  const list = [...enabledOrder]
  const from = list.indexOf(fromId)
  const to = list.indexOf(toId)
  if (from < 0 || to < 0) return list
  const [item] = list.splice(from, 1)
  list.splice(to, 0, item)
  return list
}

export function setWordLookupContentEnabled (enabledOrder, id, enabled) {
  if (enabled) {
    return enabledOrder.includes(id) ? [...enabledOrder] : [...enabledOrder, id]
  }
  return enabledOrder.filter((item) => item !== id)
}

/**
 * 将服务端/表单的 word_lookup_content_types 写入 earthworm.dictationToolbar（查词页唯一读源）
 * @param {import('vuex').Store} store
 * @param {string|string[]|null|undefined} raw — 空值表示未配置，清除本地缓存键以走默认全开
 */
export function applyWordLookupContentTypesToStore (store, raw) {
  if (!store?.state?.earthworm) return
  const prev = { ...(store.state.earthworm.dictationToolbar || {}) }
  const text = raw === undefined || raw === null ? '' : String(raw).trim()
  if (!text) {
    delete prev.word_lookup_content_types
  } else if (Array.isArray(raw)) {
    prev.word_lookup_content_types = serializeWordLookupContentTypesForApi(raw)
  } else {
    prev.word_lookup_content_types = text
  }
  store.commit('earthworm/SET_DICTATION_TOOLBAR', prev)
}

/** 从 getUserSettings.data 同步词典内容类型（始终覆盖本地，避免 localStorage 陈旧） */
export function syncWordLookupContentTypesFromApiData (store, data = {}) {
  if (!store?.state?.earthworm || !data) return
  if (!Object.prototype.hasOwnProperty.call(data, 'word_lookup_content_types')) return
  applyWordLookupContentTypesToStore(store, data.word_lookup_content_types)
}
